/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import QContext from "QContext";
import FieldListMenuContent from "qqq/components/query/FieldListMenuContent";
import React, {useContext, useEffect} from "react";


////////////////////////////////////////////////////////////////////////////////
// max height for the results list.  the list is content-height (compact), so //
// few matches -> short modal; this just caps a long field list so it scrolls //
// instead of running off-screen.  the modal is top-anchored (see below) so   //
// the title + search box stay put as the list grows/shrinks while typing.    //
////////////////////////////////////////////////////////////////////////////////
const RESULTS_LIST_MAX_HEIGHT = "320px";


export interface RecordFieldJumpModalProps
{
   open: boolean;
   onClose: () => void;
   tableMetaData: QTableMetaData;
   fieldNamesToHide: string[];
   onJumpToField: (fieldName: string) => void;
}

/***************************************************************************
 ** Centered modal that opens on the Record Screen (via the "," shortcut),
 ** lists the fields shown on the current screen, and jumps to the chosen
 ** field.  Reuses FieldListMenuContent (shared with the Query Screen's
 ** column menu) for search / filter / keyboard-nav; renders its own header
 ** (title + close) so it reads as a modal.
 ***************************************************************************/
export default function RecordFieldJumpModal({open, onClose, tableMetaData, fieldNamesToHide, onJumpToField}: RecordFieldJumpModalProps): JSX.Element
{
   ////////////////////////////////////////////////////////////////////////////////
   // omit ALL exposed joins: the record screen only renders this table's own    //
   // fields, so joined-table fields aren't on-screen (nothing to scroll to) and //
   // their presence is also what makes FieldListMenuContent draw per-table      //
   // group headers.  omitting them yields a flat list of jumpable fields, the   //
   // same on every table regardless of whether it has exposed joins.            //
   ////////////////////////////////////////////////////////////////////////////////
   const omitExposedJoins = (tableMetaData.exposedJoins ?? []).map((exposedJoin) => exposedJoin.joinTable.name);

   const {pushModalOnStack, popModalOffStack} = useContext(QContext);

   /////////////////////////////////////////////////////////////////////////////
   // while open, register in QContext's modalStack so the Record Screen's    //
   // keydown shortcuts (n/e/d/a) stay suppressed - they only fire when       //
   // modalStack.length === 0.  a plain MUI Dialog doesn't push/pop the stack //
   // on its own, so without this, moving focus to a non-input element in the //
   // modal (e.g. the close button) leaves those shortcuts live behind it.    //
   /////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (open)
      {
         pushModalOnStack?.("recordFieldJumpModal");
         return () =>
         {
            popModalOffStack?.("recordFieldJumpModal");
         };
      }
   }, [open]);

   return (
      <Dialog
         open={open}
         onClose={onClose}
         data-qqq-id="record-field-jump-modal"
         sx={{
            ////////////////////////////////////////////////////////////////////////
            // anchor the modal near the top instead of vertical-centering it, so //
            // the title + search box stay put as the (content-height) results    //
            // list grows/shrinks while typing - a centered Dialog re-centers and //
            // the input jumps.                                                   //
            ////////////////////////////////////////////////////////////////////////
            "& .MuiDialog-container": {
               alignItems: "flex-start",
            },
         }}
         PaperProps={{sx: {
            width: "400px",
            maxWidth: "100%",
            marginTop: "30vh",
            maxHeight: "80vh",
            //////////////////////////////////////////////////////////////////////
            // FieldListMenuContent is sized & dense for an anchored Menu; make //
            // it fill this (narrower) Dialog and shrink the field-row font     //
            // back to menu density.                                            //
            //////////////////////////////////////////////////////////////////////
            "& .fieldListMenuBody-recordFieldJump": {
               width: "100%",
            },
            "& .fieldListMenuBody-recordFieldJump .MuiList-root": {
               padding: 0,
            },
            "& .fieldListMenuBody-recordFieldJump .MuiListItem-root": {
               fontSize: "0.875rem",
               lineHeight: 1.35,
            },
            //////////////////////////////////////////////////////////////////////////////
            // the results list: content-height, capped at RESULTS_LIST_MAX_HEIGHT so a //
            // long field list scrolls instead of running off-screen; left/top/bottom   //
            // padding gives the row selection-indicator breathing room; no negative    //
            // right-margin (a Menu-edge trick) that would clip the scrollbar against   //
            // the rounded Dialog Paper.                                                //
            //////////////////////////////////////////////////////////////////////////////
            "& .fieldListMenuBody-recordFieldJump > .MuiBox-root:last-child": {
               margin: 0,
               overflowX: "hidden",
               //////////////////////////////////////////////////////////////////////////////
               // overflow only when the list is taller than the cap; no reserved right    //
               // gutter - let the scrollbar take its own space naturally when it appears. //
               //////////////////////////////////////////////////////////////////////////////
               overflowY: "auto",
               //////////////////////////////////////////////////////////////////////////////
               // override the shared list Box's "stable" gutter (reserves scrollbar space //
               // even when unused) so the modal doesn't carry a permanent right gap.      //
               //////////////////////////////////////////////////////////////////////////////
               scrollbarGutter: "auto",
               maxHeight: RESULTS_LIST_MAX_HEIGHT,
               padding: "0.25rem 0 0.5rem 0.5rem",
            },
            ////////////////////////////////////////////////////////////////////
            // hide the search field's in-field clear 'x' so the header close //
            // 'x' is the only x in the modal.  target the clear button as    //
            // the sibling that immediately follows the search TextField (not //
            // "first child"), so an added heading Box wouldn't misdirect it. //
            ////////////////////////////////////////////////////////////////////
            "& .fieldListMenuBody-recordFieldJump .MuiTextField-root + .MuiIconButton-root": {
               display: "none",
            },
         }}}
      >
         <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 1.75, pb: 0.75}}>
            <Box sx={{fontWeight: 700}}>Jump to a Field</Box>
            <IconButton aria-label="Close" onClick={onClose} size="small" sx={{color: "var(--qqq-grey-600, #757575)"}}>
               <Icon fontSize="small">close</Icon>
            </IconButton>
         </Box>
         <FieldListMenuContent
            idPrefix="recordFieldJump"
            placeholder="Search fields"
            tableMetaData={tableMetaData}
            fieldNamesToHide={fieldNamesToHide}
            omitExposedJoins={omitExposedJoins}
            isModeSelectOne={true}
            handleSelectedField={(field) => onJumpToField(field.name)}
            fieldEndAdornment={<Tooltip title="Jump to Field"><Icon sx={{color: "var(--qqq-grey-600, #757575)"}}>my_location</Icon></Tooltip>}
            handleAdornmentClick={(field) => onJumpToField(field.name)}
            isOpen={open}
            onClose={onClose}
         />
      </Dialog>
   );
}
